/**
 * Column Templates — vistas fijas por pantalla (PUBLICACIONES, DATA EXPORT).
 * Spec: PT-#67. Un template define QUÉ columnas se muestran (on/off) y en QUÉ
 * orden, para una pantalla concreta. Se embebe en el doc columnConfig del cliente
 * (mismo mecanismo que filterLayout) — NO es una colección aparte.
 *
 * El "Registro" (core/native/tags de columnConfig) determina qué columnas están
 * HABILITADAS a nivel cliente; el `order` del registro es solo un default/seed
 * para templates nuevos, no el orden final. El orden final lo dicta el template.
 *
 * Estas funciones son puras (sin I/O) → unit-testeables. Sirven a AMBOS paths de
 * export: PUBS reordena las columnas vivas de TanStack por el template; EXPORTAR
 * reordena los descriptores de buildExportColumns por el template.
 */

import type { ExportAttrs } from './columnExportAttrs';

export type ColumnTemplateScreenId = 'publicaciones' | 'data_export';

export interface ColumnTemplateItem {
  /** id de columna: matchea el leaf column id de TanStack / el id de exportColumns. */
  id: string;
  /** on/off dentro de este template (independiente del enabled del registro). */
  enabled: boolean;
  /** ordinal (0-based). El orden final se resuelve ordenando por este campo. */
  order: number;
  /**
   * PT-#70: override de atributos de export (title/format/widthChars) a nivel template.
   * Es el nivel de MÁS prioridad de la cadena (template.export ?? columnConfig.export ??
   * DEFAULT ?? hardcoded). Read-only por ahora (editable a futuro). Ver columnExportAttrs.ts.
   */
  export?: ExportAttrs;
}

export interface ColumnTemplate {
  items: ColumnTemplateItem[];
  updatedAt?: string;
}

export type ColumnTemplatesMap = Partial<Record<ColumnTemplateScreenId, ColumnTemplate>>;

export interface TemplateScreenMeta {
  id: ColumnTemplateScreenId;
  /** Etiqueta user-facing (mayúsculas, corta). */
  label: string;
  description: string;
}

/** Las 2 vistas fijas que existen hoy. ABM futuro puede agregar más. */
export const TEMPLATE_SCREENS: TemplateScreenMeta[] = [
  {
    id: 'publicaciones',
    label: 'PUBLICACIONES',
    description: 'Impacta el listado y el export de la pantalla Publicaciones.',
  },
  {
    id: 'data_export',
    label: 'DATA EXPORT',
    description: 'Base de columnas para el módulo Exportar.',
  },
];

/**
 * Seed de items para un template nuevo: todas las columnas disponibles,
 * enabled=true, en el orden en que vienen (el default del registro).
 */
export function seedTemplateItems(availableIds: string[]): ColumnTemplateItem[] {
  return availableIds.map((id, i) => ({ id, enabled: true, order: i }));
}

/**
 * Resuelve el orden final de columnas para una pantalla.
 *
 * @param availableIds  ids de columnas realmente disponibles (habilitadas en el
 *                      registro y presentes en el schema/datos actuales), en su
 *                      orden default.
 * @param template      el template de la pantalla (puede no existir todavía).
 * @returns lista ordenada de ids a renderizar/exportar.
 *
 * Reglas:
 * - Sin template (o vacío) → devuelve availableIds tal cual (fallback = comportamiento actual).
 * - Con template → items enabled Y disponibles, ordenados por `order`.
 *   - Items del template que ya no están disponibles → se descartan.
 *   - Items del template enabled=false → se excluyen.
 *   - Columnas disponibles que NO están en el template (columnas nuevas que
 *     aparecieron después de crear el template) → se agregan al final, enabled,
 *     en el orden de availableIds (nunca se pierden columnas nuevas en silencio).
 */
export function resolveTemplateOrder(
  availableIds: string[],
  template?: ColumnTemplate
): string[] {
  if (!template || !Array.isArray(template.items) || template.items.length === 0) {
    return [...availableIds];
  }
  const availableSet = new Set(availableIds);
  const inTemplate = new Set(template.items.map((it) => it.id));

  const ordered = [...template.items]
    .sort((a, b) => a.order - b.order)
    .filter((it) => it.enabled && availableSet.has(it.id))
    .map((it) => it.id);

  // Columnas nuevas (disponibles pero ausentes del template) → append enabled.
  const appended = availableIds.filter((id) => !inTemplate.has(id));

  return [...ordered, ...appended];
}

/**
 * Reconcilia un template existente contra las columnas disponibles actuales:
 * conserva order/enabled de los items existentes que siguen disponibles,
 * agrega los nuevos al final (enabled), y descarta los que ya no existen.
 * Devuelve items con `order` normalizado a 0..n-1. Útil al abrir el editor.
 */
export function reconcileTemplateItems(
  availableIds: string[],
  template?: ColumnTemplate
): ColumnTemplateItem[] {
  const availableSet = new Set(availableIds);
  const existing = template?.items ?? [];
  const byId = new Map(existing.map((it) => [it.id, it]));

  const kept = [...existing]
    .filter((it) => availableSet.has(it.id))
    .sort((a, b) => a.order - b.order);

  const appended: ColumnTemplateItem[] = availableIds
    .filter((id) => !byId.has(id))
    .map((id) => ({ id, enabled: true, order: 0 }));

  return [...kept, ...appended].map((it, i) => ({ ...it, order: i }));
}

/** Lee el template de una pantalla desde el mapa (o undefined). */
export function getScreenTemplate(
  templates: ColumnTemplatesMap | undefined,
  screenId: ColumnTemplateScreenId
): ColumnTemplate | undefined {
  return templates?.[screenId];
}
